import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import type { FilterType } from "@/components/@types";

type WidgetFilterOverridesContextValue = {
  overrides: Record<string, FilterType[]>;
  setWidgetFilters: (widgetId: string, filters: FilterType[]) => void;
};

const WidgetFilterOverridesContext =
  createContext<WidgetFilterOverridesContextValue | null>(null);

export function ReportsViewWidgetFilterOverridesProvider({
  children,
  resetKey,
}: {
  children: ReactNode;
  resetKey: string;
}) {
  const [overrides, setOverrides] = useState<Record<string, FilterType[]>>({});

  useEffect(() => {
    setOverrides({});
  }, [resetKey]);

  const setWidgetFilters = useCallback(
    (widgetId: string, filters: FilterType[]) => {
      setOverrides((prev) => ({ ...prev, [widgetId]: filters }));
    },
    [],
  );

  const value = useMemo(
    () => ({ overrides, setWidgetFilters }),
    [overrides, setWidgetFilters],
  );

  return (
    <WidgetFilterOverridesContext.Provider value={value}>
      {children}
    </WidgetFilterOverridesContext.Provider>
  );
}

export function useReportsViewWidgetFilterOverrides() {
  const ctx = useContext(WidgetFilterOverridesContext);
  if (!ctx) {
    throw new Error(
      "useReportsViewWidgetFilterOverrides requires ReportsViewWidgetFilterOverridesProvider",
    );
  }
  return ctx;
}

export function useReportsViewWidgetFilterOverride(
  widgetId: string | undefined,
): FilterType[] | undefined {
  const ctx = useContext(WidgetFilterOverridesContext);
  if (!widgetId || !ctx) return undefined;
  return ctx.overrides[widgetId];
}
